/* eslint-disable react-hooks/rules-of-hooks */
import { useSelector } from "react-redux";
import type { ReactNode } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Stack } from "@mui/material";

import type { RootState } from "~/redux/store";
import { formatCurrency } from "~/utils/currencyFormatter";
import { INVALID_PACKAGE_CODE } from "~/utils/constants";
import { Permission, userHasPermission } from "~/utils/permissions";
import { defaultSelectBestPackage, PSSizeMode } from "~/utils/cartHelper";
import { selectPriceScalingElement } from "~/utils/pricePicker";
import { Alert } from "~/common/m3/Alert/Alert";
import { LinkType } from "~/common/components/Link";
import CenterContents from "~/common/components/CenterContents";
import { Price as PriceV3 } from "~/common/components/Price/v3/Price";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { isKITProduct, type ProductData } from "~/models/ProductData";
import { Product } from "~/feature/Product";
import { ProductContext } from "~/feature/Product/ProductContext";
import { BasicAddToCartButton } from "~/feature/Product/elements/AddToCartButton";
import { BasicAddToWishlistButton } from "~/feature/Product/elements/AddToWishlistButton";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";
import {
  getNetPrice,
  getPackagingInformation,
  isProductPMA,
} from "~/feature/ProductDescriptor/v1/productDescriptorUtils";
import { usePurchasePlace, usePurchaseType } from "~/feature/Analytics/ga/recommendations/RecommendationContexts";
import { ProductService } from "~/AddToCart/ProductService";
import { getInitialPriceReference } from "~/AddToCart/redux/addToCart/getInitialPriceReference";
import { selectProperAmountInCart } from "~/AddToCart/redux/cartAmounts/cartAmountSelectors";
import { makeInitialOptionalPartsFormData } from "~/AddToCart/hooks/useAddToCartNew";
import { SaleTag } from "~/SalesCampaign/components/SaleTag/SaleTag";
import { calculateSalesCampaign4PDP, productHasAvailableCampaign } from "~/SalesCampaign/utils";
import { SaleQuantityIndicator } from "~/SalesCampaign/components/SaleQuantityIndicator/SaleQuantityIndicator";
import classes from "./ProductRecommendationTile.module.less";
import { ProductComparisonCheckbox } from "~/ProductComparison/ProductComparisonCheckbox";
import { isTariffSurchargeEnabled } from "~/Cart/cartUtils";

export interface trackingAttributesProps {
  rootTrackingAttributes?: any;
  linkProps: any;
  buttonProps: any;
  wishListBtnProps?: any;
  gaLocation?: string;
}

interface ProductTileProps {
  productCode: string;
  squeezePrice?: boolean;
  itemWidth: number;
  variant: string;
  getTrackingAttributes: (
    product: ProductData,
    productDescriptor: ProductDescriptor,
    isPMA: boolean,
  ) => trackingAttributesProps;
}

export const ProductRecommendationsTile = ({
  productCode,
  squeezePrice,
  itemWidth,
  variant,
  getTrackingAttributes,
}: ProductTileProps) => {
  const { t } = useTranslation();
  const reco_type = usePurchaseType();
  const reco_place = usePurchasePlace();

  const isCampaignReco = variant.includes("campaign");
  const isRecentlyViewedReco = variant === "recently-viewed";

  const priceReference = getInitialPriceReference();
  const productDescriptor = ProductService.useProductDescriptor({
    productCode: productCode,
    priceReference,
    options: { fetch: true },
  });
  const product = productDescriptor.base;

  const isPMA = isProductPMA(productDescriptor);
  const hasSales = productHasAvailableCampaign(product.salesCampaign);

  const {
    rootTrackingAttributes,
    linkProps,
    buttonProps,
    wishListBtnProps,
    gaLocation = "",
  } = getTrackingAttributes(product, productDescriptor, isPMA);

  const getGeneralPriceElement = () => {
    const packaging = defaultSelectBestPackage(
      1,
      productDescriptor.availability?.packagingInformation?.map((p) => p.packCode) ?? [],
      productDescriptor,
      PSSizeMode.SMALLEST_FIRST,
    );
    const packCode = packaging?.packCode ?? INVALID_PACKAGE_CODE;

    const cartQuantity = useSelector((state: RootState) => {
      return selectProperAmountInCart({ priceReference, productCode: productCode, packCode })(state);
    });

    let priceElement: ReactNode;
    if (!userHasPermission(Permission.PRICE)) {
      priceElement = null;
    } else {
      if (productDescriptor.isPriceError) {
        priceElement = <Alert severity={"error"}>{"Error loading data"}</Alert>;
      } else if (productDescriptor.isPriceLoading) {
        priceElement = (
          <CenterContents>
            <CircleSpinner />
          </CenterContents>
        );
      } else {
        const totalQuantity = cartQuantity + (packaging?.packSize ?? 0);
        const currentPrice = getNetPrice({
          packCode,
          product: productDescriptor,
          totalQuantity,
        });
        const tariffSurcharge = isTariffSurchargeEnabled() && currentPrice.tariffSurcharge;
        const textPrice = formatCurrency(currentPrice.value, currentPrice.currencyIso);
        const longPrice = textPrice.length > 15;
        const zeroPrice = currentPrice.value === 0;
        priceElement = (
          <div className={classes.prices}>
            <PriceV3
              price={currentPrice}
              className={classNames(
                classes.price,
                squeezePrice && longPrice && classes.squeezePrice,
                zeroPrice && classes.priceOnRequest,
              )}
              zeroText={t("product.priceOnRequest")}
              tariffSurcharge={tariffSurcharge}
            />
            {tariffSurcharge && <p className="label-small">{t("price.tariff.surcharge.applicable")}</p>}
          </div>
        );
      }
    }
    return priceElement;
  };

  const getCampaignPriceElement = () => {
    const salesMap = calculateSalesCampaign4PDP({
      productDescriptor,
    });
    const cartQuantity = useSelector((state: RootState) => {
      if (salesMap.status !== "loaded") {
        return 0;
      }
      const { salePerPack } = salesMap.data;
      const entries = Object.entries(salePerPack);
      if (!entries.length) {
        return 0;
      }
      const [packCode] = entries[0];
      return selectProperAmountInCart({ priceReference, productCode: product.code, packCode })(state);
    });

    let priceElements: ReactNode;
    if (salesMap.status === "error" || productDescriptor.isError) {
      priceElements = <Alert severity={"error"}>{"Error loading data"}</Alert>;
    } else if (salesMap.status === "loaded") {
      const { salePerPack } = salesMap.data;
      const entries = Object.entries(salePerPack);
      if (!entries.length) {
        return null;
      }
      const [packCode, salesData] = Object.entries(salePerPack)[0];
      if (!packCode) {
        priceElements = <Alert severity={"error"}>{"Error loading data"}</Alert>;
      } else {
        const packaging = getPackagingInformation(productDescriptor, packCode);
        const totalQuantity = cartQuantity + (packaging?.packSize ?? 0);
        const netPrice = getNetPrice({
          packCode,
          product: productDescriptor,
          totalQuantity,
        });
        const salesPrice = selectPriceScalingElement(totalQuantity, salesData.price)?.price;
        const isSamePrice = salesPrice?.value === netPrice.value;

        const currentPrice = salesPrice ?? netPrice;
        const textPrice = formatCurrency(currentPrice.value, currentPrice.currencyIso);
        const longPrice = textPrice.length > 15;
        const tariffSurcharge = isTariffSurchargeEnabled() && netPrice.tariffSurcharge;
        priceElements = (
          <div className={classes.prices}>
            <SaleQuantityIndicator quantity={salesData.salesStock} className={classes.quantity} />
            <PriceV3
              price={netPrice}
              tariffSurcharge={tariffSurcharge}
              className={classNames(classes.oldPrice, isSamePrice && "v-hidden")}
            />
            <PriceV3
              price={currentPrice}
              tariffSurcharge={tariffSurcharge}
              className={classNames(classes.newPrice, squeezePrice && longPrice && classes.squeezePrice)}
            />
            {tariffSurcharge && <p className="label-small">{t("price.tariff.surcharge.applicable")}</p>}
          </div>
        );
      }
    } else {
      priceElements = (
        <CenterContents>
          <CircleSpinner />
        </CenterContents>
      );
    }
    return priceElements;
  };

  let buttonElement;
  if (userHasPermission(Permission.ADD_TO_CART)) {
    let finalInitialData = undefined;
    if (isRecentlyViewedReco) {
      const isHousingKit = isKITProduct(product);
      const optionalParts = (isHousingKit && product.optionalParts) || [];
      const selectedPack = productDescriptor.availability?.packagingInformation[0];
      finalInitialData = isHousingKit
        ? {
            quantity: selectedPack?.packSize ?? undefined,
            priceReference: getInitialPriceReference(),
            selectedPackCode: selectedPack?.packCode ?? undefined,
            optionalParts: makeInitialOptionalPartsFormData(optionalParts, 1),
            selectedPack,
            totalQuantity: selectedPack?.packSize ?? undefined,
          }
        : undefined;
    }
    buttonElement = (
      <BasicAddToCartButton
        fullWidth
        size={"medium"}
        className={classes.button}
        disabled={productDescriptor.isLoading || productDescriptor.isError || productDescriptor.isPriceLoading}
        actionVariant={isRecentlyViewedReco ? (isPMA ? "quote" : "cart") : undefined}
        wizardConfig={finalInitialData ? { initialData: finalInitialData } : undefined}
        reco_place={reco_place}
        {...buttonProps}
      />
    );
  } else if (userHasPermission(Permission.ADD_TO_WISHLIST)) {
    buttonElement = (
      <BasicAddToWishlistButton fullWidth className={classes.button} size={"medium"} {...wishListBtnProps} />
    );
  }

  return (
    <ProductContext value={product}>
      <div className={classes.root} style={{ width: `${itemWidth}px` }}>
        {(isCampaignReco || hasSales) && <SaleTag className={classes.saleTag} />}
        <div className={classes.productCard} {...rootTrackingAttributes}>
          <Product.PrimaryImage className={classes.productImage} linkProps={linkProps} />

          <Stack sx={{ gap: 0 }}>
            <Product.PdpLink reco_type={reco_type} {...linkProps} variant={LinkType.NAVIGATION}>
              <Product.FullDesignation className={classes.designation} truncate clickable={false} />
            </Product.PdpLink>

            <Product.PdpLink reco_type={reco_type} {...linkProps}>
              <Product.ShortDescription truncate clickable={false} />
            </Product.PdpLink>
          </Stack>

          {isCampaignReco || hasSales ? getCampaignPriceElement() : getGeneralPriceElement()}

          <ProductComparisonCheckbox
            labelName={t("product.reco.addToComparison")}
            gaLocation={gaLocation}
            productCode={product.code}
          />

          {buttonElement}
        </div>
      </div>
    </ProductContext>
  );
};
